import React from 'react';
import { connect } from 'react-redux';
import { SurveyStates } from '../../../../common/src/enums/SurveyStates';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchGet } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { formatBytes } from '../../utils/formatBytes';
import { isAdministratorPortal } from '../../utils/getGlobalSettings';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { appContext } from '../appContext';
import { Content, LeftTray, LeftTrayItem, LeftTrayList, Panel, PanelBody, PanelHeading, PanelWithoutForm, Tray } from '../elements';
import { FlightLogs } from '../FlightLog/FlightLog';
import { Component } from '../utils/Component';
import { PieChart, PieChartItem } from '../utils/PieChart';
import RoleDependent from '../utils/RoleDependent';
import { surveyStatusToColorCode } from '../utils/surveyStatusToColorCode';
import { DashboardAssignedSurveysList } from './DashboardAssignedSurveysList';
import { CompanyUsagesList } from './DashboardCompanyUsagesList';
import { DashboardEquipmentExpirations } from './DashboardEquipmentExpiration';
import { DashboardMfaPopup } from './DashboardMfaPopup';
import { DashboardSurveysList } from './DashboardSurveysList';

interface IDashboardProps extends ITranslateProps, IRouterProps {
    dispatch: (prop: any) => void;
    hasOtpEnabled: boolean;
    userRole: Roles;
    isDroneOperator: boolean;
    isClient: boolean;
    shouldEnforceMfa: boolean;
    useEquipmentManagement: boolean;
}

interface IDashboardState {
    message?: string;
    errorCode?: string;
    name?: string;
    storageMb?: number;
    storageUsed?: number;
    status?: string;
    role?: string;
    state?: string;
    city?: string;
    zip?: string;
    street?: string;
    country?: string;
    isClient?: boolean;
    chartDataProgress: PieChartItem[];
    daysVisibilityFutureSurveys?: number;
    useProductTitle?: boolean;
}

function mapStateToProps(state: any) {
    const { authentication } = state;

    return {
        isDroneOperator: authentication.isDroneOperator,
        hasOtpEnabled: authentication.hasOtpEnabled,
        shouldEnforceMfa: authentication.shouldEnforceMfa,
        userRole: authentication.role,
        useEquipmentManagement: authentication.useEquipmentManagement,
        isClient: authentication.isClient,
    };
}

class CompanyDetails extends Component<IDashboardProps, IDashboardState> {
    constructor(props: IDashboardProps) {
        super(props);

        this.state = {
            chartDataProgress: [],
        };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override render() {
        return (
            <appContext.Consumer>
                {(appState) => {
                    if (isAdministratorPortal(appState.globalSettings)) {
                        return (
                            <Content>
                                <LeftTray>
                                    <Panel>
                                        <PanelBody>Welcome to the administrator portal.</PanelBody>
                                    </Panel>
                                </LeftTray>
                            </Content>
                        );
                    }

                    return (
                        <Content>
                            <LeftTray>
                                <Panel>
                                    <PanelBody>
                                        <address>
                                            <br />
                                            <strong>{this.state.name}</strong>
                                            <br />
                                            {this.state.street}
                                            <br />
                                            {this.state.zip}&nbsp; {this.state.city}
                                        </address>

                                        <RoleDependent
                                            allowed={[Roles.Client]}
                                            content={
                                                <LeftTrayList>
                                                    <LeftTrayItem title={this.props.t('dashboard.storageUsed')}>
                                                        {formatBytes(this.state.storageUsed ?? 0)}
                                                    </LeftTrayItem>
                                                </LeftTrayList>
                                            }
                                        />
                                    </PanelBody>
                                </Panel>

                                {this.state.chartDataProgress && this.state.chartDataProgress.length > 0 && (
                                    <Panel>
                                        <PanelBody noPadding>
                                            <div className="p10">
                                                <h4> {this.props.t('common.progress')}</h4>
                                                <PieChart data={this.state.chartDataProgress} onClick={this.onPieChartClicked.bind(this)} />
                                            </div>
                                        </PanelBody>
                                    </Panel>
                                )}
                            </LeftTray>

                            <Tray>
                                {this.props.shouldEnforceMfa && !this.props.hasOtpEnabled && <DashboardMfaPopup />}

                                <DashboardAssignedSurveysList useProductTitle={this.state.useProductTitle} />
                                {this.props.useEquipmentManagement && this.props.isClient && <DashboardEquipmentExpirations />}

                                {this.props.isDroneOperator && (
                                    <RoleDependent
                                        allowed={[Roles.Administrator, Roles.Client]}
                                        content={
                                            <PanelWithoutForm>
                                                <FlightLogs header={this.props.t('dashboard.flightLogs')} itemsPerPage={20} />
                                            </PanelWithoutForm>
                                        }
                                    />
                                )}
                                {this.state.daysVisibilityFutureSurveys && (
                                    <DashboardSurveysList
                                        daysVisibilityFutureSurveys={this.state.daysVisibilityFutureSurveys}
                                        useProductTitle={this.state.useProductTitle}
                                    ></DashboardSurveysList>
                                )}
                                {isCustomerVersion(['hyperplane', 'intures', 'aquasmart']) && (
                                    <RoleDependent
                                        allowed={[Roles.Client]}
                                        content={
                                            <PanelWithoutForm>
                                                <PanelHeading icon="fa fa-dashboard" text={this.props.t('dashboard.companyUsagesList')} />
                                                <CompanyUsagesList />
                                            </PanelWithoutForm>
                                        }
                                    />
                                )}
                            </Tray>
                        </Content>
                    );
                }}
            </appContext.Consumer>
        );
    }

    private async load() {
        const url = '/api/user/company';
        const result = await fetchGet(url, {}, [], [403]);
        await this.setState(result.json);
        if (result.json?.storageLimit) {
            await this.setState({ storageMb: Math.ceil(result.json.storageLimit / 1024 / 1024) });
        }

        // Basf reports a problem with dashboard that cannot be reproduced. Seems from progress indicator. This is disabled for Basf.
        if (!this.state.name?.toLowerCase().includes('basf')) {
            const progressSurveys = await fetchGet<{ items: { count: number; status: SurveyStates }[] }>(
                '/api/surveys/statistics/progress',
                {},
                [],
                [403],
            );

            if (progressSurveys.json?.items) {
                const progressChartData: PieChartItem[] = progressSurveys.json.items.map((i) => ({
                    factor: i.count,
                    title: this.props.t(`surveyStatus.${i.status}`),
                    id: i.status,
                    color: surveyStatusToColorCode(i.status),
                }));
                await this.setState({ chartDataProgress: progressChartData });
            }
        }
    }

    private onPieChartClicked = (item: PieChartItem) => {
        this.props.navigate(`/surveys?status=${item.id}`);
    };
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(CompanyDetails as any) as any);
